/**
 * LiveFieldHero — compact today-focused team band for V2 dashboard.
 *
 * Shows EVERYONE who's relevant today: live field techs, on-leave techs,
 * day-off techs (per workHours config). 2-row responsive grid, compact
 * cards. Pulls real data from useJobs + useTechnicians + useTeamLocations.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Radio, MapPin } from 'lucide-react';
import { useJobs } from '@/hooks/useJobs';
import { useTechnicians } from '@/hooks/useTeam';
import { useTeamLocations } from '@/hooks/useTeamLocations';
import { useScheduleEvents } from '@/hooks/useScheduleEvents';
import type { Job } from '@/types';
import type { TeamMember } from '@/hooks/useTeam';
import type { ScheduleEvent, ScheduleEventType } from '@/types/schema';
import { CustomerLink } from '@/components/customers/CustomerLink';

type LiveState =
  | 'working'
  | 'on_site'
  | 'en_route'
  | 'wrapping_up'
  | 'scheduled'
  | 'idle'      // working today but no current job
  | 'leave'    // sick / annual / training
  | 'off';     // day off per workHours

interface TechCard {
  techId: string;
  name: string;
  initials: string;
  state: LiveState;
  currentJob?: Job;
  jobsToday: number;
  jobsCompleted: number;
  recentlyLive: boolean;
  reason?: string; // leave / off context
}

const STATE_CONFIG: Record<
  LiveState,
  { label: string; dot: string; bg: string; text: string; intensity: 'high' | 'med' | 'low' }
> = {
  working:     { label: 'Working',    dot: 'bg-emerald-400', bg: 'bg-emerald-500/15', text: 'text-emerald-300', intensity: 'high' },
  on_site:     { label: 'On site',    dot: 'bg-emerald-400', bg: 'bg-emerald-500/15', text: 'text-emerald-300', intensity: 'high' },
  en_route:    { label: 'En route',   dot: 'bg-blue-400',    bg: 'bg-blue-500/15',    text: 'text-blue-300',    intensity: 'high' },
  wrapping_up: { label: 'Wrapping',   dot: 'bg-amber-400',   bg: 'bg-amber-500/15',   text: 'text-amber-300',   intensity: 'high' },
  scheduled:   { label: 'Next up',    dot: 'bg-purple-400',  bg: 'bg-purple-500/15',  text: 'text-purple-300',  intensity: 'med' },
  idle:        { label: 'Free',       dot: 'bg-slate-400',   bg: 'bg-slate-500/15',   text: 'text-slate-300',   intensity: 'med' },
  leave:       { label: 'On leave',   dot: 'bg-rose-400/60', bg: 'bg-rose-500/[0.08]',text: 'text-rose-300/70', intensity: 'low' },
  off:         { label: 'Day off',    dot: 'bg-slate-500/40',bg: 'bg-slate-500/[0.06]',text: 'text-slate-400/70', intensity: 'low' },
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function todayLocalDateStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Map JS day-of-week (Sun=0..Sat=6) to workHours index (Mon=0..Fri=4); -1 for weekend. */
function workDayIndex(d: Date): number {
  const js = d.getDay();
  if (js === 0 || js === 6) return -1;
  return js - 1;
}

function eventLabel(type: ScheduleEventType, title?: string): string {
  // Prefer the user-set title if it's not just the auto-generated type label.
  if (title && title.trim() && title.toLowerCase() !== type) return title;
  switch (type) {
    case 'leave':          return 'On leave';
    case 'sick':           return 'Sick';
    case 'training':       return 'Training';
    case 'meeting':        return 'In meeting';
    case 'task':           return 'On task';
    case 'block':          return 'Blocked';
    case 'tentative_job':  return 'Tentative slot';
    case 'vehicle':        return 'Vehicle service';
    default:               return type;
  }
}

function deriveTechState(
  tech: TeamMember,
  todaysJobs: Job[],
  recentlyLive: boolean,
  todaysEvents: ScheduleEvent[],
): TechCard {
  const base = {
    techId: tech.id,
    name: tech.name,
    initials: getInitials(tech.name),
    jobsToday: 0,
    jobsCompleted: 0,
    recentlyLive: false,
  };

  // 0. A full-day schedule event for this tech overrides everything else
  //    (e.g., training, sick day logged via /schedule). Sub-day events with
  //    startTime are not full-day blockers — let them through to step 3.
  const fullDayEvent = todaysEvents.find((e) => {
    const appliesToTech = e.techIds.length === 0 || e.techIds.includes(tech.id);
    const isFullDay = !e.startTime;
    return appliesToTech && isFullDay;
  });
  if (fullDayEvent) {
    const isOffType = fullDayEvent.type === 'leave' || fullDayEvent.type === 'sick' ||
                       fullDayEvent.type === 'training';
    return {
      ...base,
      state: isOffType ? 'leave' : 'off',
      reason: eventLabel(fullDayEvent.type, fullDayEvent.title),
    };
  }

  // 1. Explicit leave status (Sick / Annual / Personal / Training set on user)
  if (tech.status) {
    return { ...base, state: 'leave', reason: tech.status };
  }

  // 2. Day off (workHours configured to null for today)
  const today = new Date();
  const wdi = workDayIndex(today);
  if (wdi === -1) {
    return { ...base, state: 'off', reason: 'Weekend' };
  }
  if (tech.workHours && tech.workHours[wdi] === null) {
    return { ...base, state: 'off', reason: 'Off today' };
  }

  // 3. Active — find their current/next job today
  const myJobs = todaysJobs.filter((j) => {
    if (j.techId === tech.id) return true;
    if (Array.isArray(j.techIds) && j.techIds.includes(tech.id)) return true;
    return false;
  });

  const completed = myJobs.filter(
    (j) => j.jobState === 'COMPLETED' || j.status === 'Completed',
  ).length;

  const workingJob   = myJobs.find((j) => j.jobState === 'WORKING');
  const onSiteJob    = myJobs.find((j) => j.jobState === 'ON_SITE');
  const wrappingJob  = myJobs.find((j) => j.jobState === 'WRAPPING_UP');
  const enRouteJob   = myJobs.find((j) => j.jobState === 'EN_ROUTE');
  const scheduledJob = myJobs.find(
    (j) => j.jobState === 'SCHEDULED' || (!j.jobState && j.status !== 'Completed'),
  );

  let state: LiveState = 'idle';
  let currentJob: Job | undefined;

  if (workingJob)         { state = 'working';     currentJob = workingJob; }
  else if (onSiteJob)     { state = 'on_site';     currentJob = onSiteJob; }
  else if (wrappingJob)   { state = 'wrapping_up'; currentJob = wrappingJob; }
  else if (enRouteJob)    { state = 'en_route';    currentJob = enRouteJob; }
  else if (scheduledJob)  { state = 'scheduled';   currentJob = scheduledJob; }

  return {
    ...base,
    state,
    currentJob,
    jobsToday: myJobs.length,
    jobsCompleted: completed,
    recentlyLive,
  };
}

const STATE_ORDER: Record<LiveState, number> = {
  working: 0, on_site: 1, wrapping_up: 2, en_route: 3, scheduled: 4, idle: 5, off: 6, leave: 7,
};

export function LiveFieldHero() {
  const navigate = useNavigate();
  const { jobs, loading: jobsLoading } = useJobs();
  const { technicians, loading: techsLoading } = useTechnicians();
  const { locations } = useTeamLocations();
  const { events: scheduleEvents } = useScheduleEvents();

  const liveTechIds = useMemo(() => {
    const set = new Set<string>();
    locations.forEach((l) => { if (l.isRecent) set.add(l.userId); });
    return set;
  }, [locations]);

  const cards = useMemo<TechCard[]>(() => {
    const today = todayLocalDateStr();
    const todaysJobs = jobs.filter((j) => j.date === today);
    const todaysEvents = scheduleEvents.filter(
      (e) => !e.isTemplate && e.date === today,
    );
    return technicians
      .map((t) => deriveTechState(t, todaysJobs, liveTechIds.has(t.id), todaysEvents))
      .sort((a, b) => {
        if (STATE_ORDER[a.state] !== STATE_ORDER[b.state]) return STATE_ORDER[a.state] - STATE_ORDER[b.state];
        return b.jobsToday - a.jobsToday;
      });
  }, [jobs, technicians, liveTechIds, scheduleEvents]);

  const isInitialLoading = (jobsLoading || techsLoading) && technicians.length === 0;

  const activeCount = cards.filter(
    (c) => c.state !== 'off' && c.state !== 'leave' && c.state !== 'idle',
  ).length;
  const offTodayCount = cards.filter((c) => c.state === 'off' || c.state === 'leave').length;
  const totalToday = cards.reduce((sum, c) => sum + c.jobsToday, 0);
  const completedToday = cards.reduce((sum, c) => sum + c.jobsCompleted, 0);

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="relative h-full rounded-3xl border border-theme bg-surface-card backdrop-blur-xl overflow-hidden shadow-[0_8px_32px_-12px_rgba(0,0,0,0.4)] flex flex-col"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none z-10" />

      {/* Header */}
      <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-theme-soft">
        <div className="flex items-center gap-2.5">
          <div className="relative w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <Radio className="w-4 h-4 text-white" strokeWidth={2.5} />
            <motion.span
              className="absolute -inset-1 rounded-xl border border-emerald-400/40"
              animate={{ scale: [1, 1.4], opacity: [0.6, 0] }}
              transition={{ duration: 1.8, repeat: Infinity }}
            />
          </div>
          <div>
            <h3 className="text-base font-bold text-theme-primary leading-tight">Live Field</h3>
            <p className="text-[11px] text-theme-muted leading-tight mt-0.5">
              {activeCount} active · {completedToday}/{totalToday} jobs done
              {offTodayCount > 0 && <> · {offTodayCount} off</>}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/home/map')}
          className="text-[11px] font-medium text-theme-muted hover:text-brand-secondary transition-colors flex items-center gap-1 px-2.5 py-1.5 rounded-lg hover:bg-surface-hover"
        >
          <MapPin className="w-3 h-3" />
          Open map
        </button>
      </header>

      {/* Compact 2-row grid — auto-fits across responsive widths */}
      <div className="p-3 grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {isInitialLoading ? (
          [0, 1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="rounded-xl border border-theme-soft bg-surface-raised/40 p-2.5 flex items-center gap-2.5 animate-pulse"
              style={{ opacity: 1 - i * 0.1 }}
            >
              <div className="w-9 h-9 rounded-full bg-surface-hover shrink-0" />
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="h-2.5 bg-surface-hover rounded w-3/4" />
                <div className="h-2 bg-surface-hover/70 rounded w-1/2" />
              </div>
            </div>
          ))
        ) : cards.length === 0 ? (
          <div className="col-span-full text-center py-6 text-xs text-theme-muted">
            No technicians configured.
          </div>
        ) : (
          cards.map((card, i) => (
            <TechMiniCard key={card.techId} card={card} index={i} onClick={() => navigate('/home/schedule')} />
          ))
        )}
      </div>
    </motion.section>
  );
}

function TechMiniCard({ card, index, onClick }: { card: TechCard; index: number; onClick: () => void }) {
  const cfg = STATE_CONFIG[card.state];
  const job = card.currentJob;
  const dimmed = cfg.intensity === 'low';

  return (
    <motion.button
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: dimmed ? 0.65 : 1, y: 0 }}
      transition={{ delay: index * 0.025, duration: 0.2 }}
      whileHover={{ y: -1, opacity: 1 }}
      onClick={onClick}
      className={`text-left rounded-xl border border-theme-soft hover:border-theme bg-surface-raised p-2.5 transition-all group flex items-center gap-2.5 min-w-0`}
    >
      {/* Avatar with state ring */}
      <div className="relative shrink-0">
        <div className={`w-9 h-9 rounded-full ${cfg.bg} flex items-center justify-center`}>
          <span className={`text-[11px] font-bold ${cfg.text}`}>{card.initials}</span>
        </div>
        {card.recentlyLive && (
          <motion.span
            className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-surface-raised"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        )}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="text-xs font-semibold text-theme-primary truncate">{card.name}</p>
          {card.jobsToday > 0 && card.state !== 'off' && card.state !== 'leave' && (
            <span className="text-[9px] text-theme-muted shrink-0">
              {card.jobsCompleted}/{card.jobsToday}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
          <span className={`text-[10px] font-medium ${cfg.text} truncate`}>
            {cfg.label}
            {card.reason && ` · ${card.reason}`}
          </span>
        </div>
        {job && (
          <p className="text-[10px] text-theme-muted truncate mt-0.5" title={`${job.client} · ${job.type} · ${job.time}`}>
            {/* CUSTOMER_360: parent <motion.button> at this card has its own
                onClick (navigate to /home/schedule). CustomerLink's default
                stopPropagation prevents that from firing when the name is
                clicked — opens the customer profile instead. */}
            <CustomerLink
              customerId={job.customerId ?? null}
              displayName={job.client || 'Untitled'}
              className="text-theme-muted"
            />
            {' · '}
            {job.time || '—'}
          </p>
        )}
      </div>
    </motion.button>
  );
}
